// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.
import React, { useState } from 'react';
import { Modal, ModalContent, ModalFooter, ModalHeader } from '@heroui/react';
import { Button } from '@/components/ui/button';
import { type ModalProps } from '@/stores/use-modal-store.ts';
import { toast } from 'sonner';
import { useAuth } from '@/context/auth-context/use-auth.ts';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { API_BASE } from '@/config.ts';
import { clearFetchCache } from '@/utils/fetch-with-cache.ts';

export const DeleteUserModal: React.FC<ModalProps> = ({ isOpen, onClose, data }) => {
  const { t } = useTranslation();
  const { userId } = data;
  const [isLoading, setIsLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const { user, logout, deleteCookiesAndSession } = useAuth();
  const navigate = useNavigate();

  const REQUIRED_TEXT = 'Я ХОЧУ УДАЛИТЬ МОЮ УЧЕТНУЮ ЗАПИСЬ';
  const isConfirmed = confirmText === REQUIRED_TEXT;

  const onClick = async () => {
    try {
      setIsLoading(true);
      if (!user?.id) {
        toast.error('No session found!');
        // checkSession();
      }
      if (userId !== user?.id) return;

      await fetch(`${API_BASE}/api/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      toast.success('Аккаунт успешно удален!');
      await logout();
      await deleteCookiesAndSession();
      try {
        // @ts-ignore
        if (window.webPage) {
          // @ts-ignore
          window.webPage.onLogoutRequested();
        }
      } catch (err) {}
      await clearFetchCache();
      navigate('/auth/login');
      onClose();
    } catch (error) {
      toast.error('Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setConfirmText('');
    onClose();
  };

  // TODO: для сервера тоже самое
  return (
    <Modal isOpen={isOpen} onOpenChange={handleClose}>
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <div className="text-center text-2xl font-bold">{t('DeleteUserModal.title')}</div>
              <div className="text-center">{t('DeleteUserModal.description')}</div>
            </ModalHeader>
            <div className="px-6 pb-2">
              <input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={REQUIRED_TEXT}
                className="w-full border rounded-md px-3 py-2 text-sm"
                disabled={isLoading}
              />
              <div className="text-xs text-gray-500 mt-1">
                Введите в поле <b>{REQUIRED_TEXT}</b> для удаления
              </div>
            </div>
            <ModalFooter>
              <Button
                disabled={isLoading}
                onClick={() => {
                  setConfirmText('');
                  onClose();
                }}
                variant="ghost"
              >
                {t('DeleteUserModal.cancel')}
              </Button>
              <Button disabled={isLoading || !isConfirmed} color="danger" onClick={onClick}>
                {t('DeleteUserModal.confirm')}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
